import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { getSupabaseClient } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  Package, 
  Plus, 
  Search, 
  AlertTriangle, 
  Loader2, 
  RefreshCw, 
  IndianRupee, 
  CheckCircle2, 
  Layers, 
  X,
  Edit2
} from 'lucide-react';

export interface ServiceMaterialProduct {
  id: string;
  tenant_id: string;
  product_name: string;
  sku?: string | null;
  barcode?: string | null;
  sale_price?: number | null;
  w_sale_price?: number | null;
  mrp?: number | null;
  purchase_price?: number | null;
  unit?: string | null;
  category_id?: string | null;
  current_quantity?: number;
  reorder_level?: number;
  is_active?: boolean;
}

export function ServicesInventoryView({ branchId }: { branchId?: string | null } = {}) {
  const { currentTenantId, activeBranchId } = useAuth();
  const supabase = getSupabaseClient();

  const [loading, setLoading] = useState(true);
  const [materials, setMaterials] = useState<ServiceMaterialProduct[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStock, setFilterStock] = useState<'all' | 'low' | 'out'>('all');

  // Add / Edit Modal
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<ServiceMaterialProduct | null>(null);
  const [name, setName] = useState('');
  const [sku, setSku] = useState('');
  const [salePrice, setSalePrice] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [currentQty, setCurrentQty] = useState('');
  const [reorderLevel, setReorderLevel] = useState('5');
  const [unit, setUnit] = useState('Nos');
  const [saving, setSaving] = useState(false);

  const effectiveBranchId = branchId || activeBranchId;

  // ----------------------------------------------------------------
  // Load Materials & Spare Parts
  // ----------------------------------------------------------------
  const loadData = useCallback(async () => {
    if (!supabase || !currentTenantId) return;
    setLoading(true);

    try {
      // Query products for this tenant with their product_stock records
      const { data, error } = await supabase
        .from('products')
        .select(`
          id,
          tenant_id,
          product_name,
          sku,
          barcode,
          w_sale_price,
          mrp,
          purchase_price,
          unit,
          category_id,
          is_active,
          product_stock (
            current_quantity,
            reorder_level,
            branch_id
          )
        `)
        .eq('tenant_id', currentTenantId)
        .order('product_name');

      if (error) {
        throw error;
      }

      const formatted: ServiceMaterialProduct[] = (data || []).map((p: any) => {
        const stocks = p.product_stock || [];
        const relevantStock = effectiveBranchId 
          ? stocks.find((s: any) => s.branch_id === effectiveBranchId) || stocks[0]
          : stocks[0];

        const totalQty = stocks.reduce((sum: number, s: any) => sum + Number(s.current_quantity || 0), 0);

        return {
          id: p.id,
          tenant_id: p.tenant_id,
          product_name: p.product_name,
          sku: p.sku,
          barcode: p.barcode,
          sale_price: p.w_sale_price || p.mrp || 0,
          w_sale_price: p.w_sale_price,
          mrp: p.mrp,
          purchase_price: p.purchase_price,
          unit: p.unit || 'Nos',
          category_id: p.category_id,
          current_quantity: relevantStock ? Number(relevantStock.current_quantity || 0) : totalQty,
          reorder_level: relevantStock ? Number(relevantStock.reorder_level || 5) : 5,
          is_active: p.is_active !== false
        };
      });

      setMaterials(formatted);
    } catch (err: any) {
      console.error('Error loading services inventory:', err);
      toast.error(err.message || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [supabase, currentTenantId, effectiveBranchId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ----------------------------------------------------------------
  // Add / Edit Material Handler
  // ----------------------------------------------------------------
  const handleOpenAdd = () => {
    setEditingItem(null);
    setName('');
    setSku('');
    setSalePrice('');
    setPurchasePrice('');
    setCurrentQty('0');
    setReorderLevel('5');
    setUnit('Nos');
    setShowModal(true);
  };

  const handleOpenEdit = (item: ServiceMaterialProduct) => {
    setEditingItem(item);
    setName(item.product_name || '');
    setSku(item.sku || '');
    setSalePrice(String(item.sale_price ?? ''));
    setPurchasePrice(String(item.purchase_price ?? ''));
    setCurrentQty(String(item.current_quantity ?? 0));
    setReorderLevel(String(item.reorder_level ?? 5));
    setUnit(item.unit || 'Nos');
    setShowModal(true);
  };

  const handleSaveItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase || !currentTenantId) return;

    if (!name.trim()) {
      toast.error('Material name is required');
      return;
    }

    setSaving(true);
    try {
      let branchToUse = effectiveBranchId;
      if (!branchToUse) {
        const { data: bData } = await supabase
          .from('branches')
          .select('id')
          .eq('tenant_id', currentTenantId)
          .limit(1)
          .maybeSingle();
        branchToUse = bData?.id || null;
      }

      if (editingItem) {
        // Update product
        const { error: updErr } = await supabase
          .from('products')
          .update({
            product_name: name.trim(),
            sku: sku.trim() || null,
            w_sale_price: Number(salePrice) || 0,
            mrp: Number(salePrice) || 0,
            purchase_price: purchasePrice ? Number(purchasePrice) : null,
            unit: unit.trim() || 'Nos'
          })
          .eq('id', editingItem.id);

        if (updErr) throw updErr;

        // Upsert stock
        if (branchToUse) {
          await supabase.from('product_stock').upsert({
            product_id: editingItem.id,
            branch_id: branchToUse,
            current_quantity: Number(currentQty) || 0,
            reorder_level: Number(reorderLevel) || 5
          }, { onConflict: 'product_id,branch_id' });
        }

        toast.success('Material updated successfully!');
      } else {
        // Insert product
        const { data: newProd, error: insErr } = await supabase
          .from('products')
          .insert({
            tenant_id: currentTenantId,
            product_name: name.trim(),
            sku: sku.trim() || null,
            w_sale_price: Number(salePrice) || 0,
            mrp: Number(salePrice) || 0,
            purchase_price: purchasePrice ? Number(purchasePrice) : null,
            unit: unit.trim() || 'Nos',
            is_active: true
          })
          .select()
          .single();

        if (insErr) throw insErr;

        if (branchToUse && newProd) {
          await supabase.from('product_stock').insert({
            product_id: newProd.id,
            branch_id: branchToUse,
            current_quantity: Number(currentQty) || 0,
            reorder_level: Number(reorderLevel) || 5
          });
        }

        toast.success('Material item added to inventory!');
      }

      setShowModal(false);
      await loadData();
    } catch (err: any) {
      console.error('Save material error:', err);
      toast.error(err.message || 'Failed to save material');
    } finally {
      setSaving(false);
    }
  };

  // Filtered List
  const filteredMaterials = materials.filter(m => {
    if (filterStock === 'low' && (m.current_quantity || 0) > (m.reorder_level || 5)) return false;
    if (filterStock === 'out' && (m.current_quantity || 0) > 0) return false;

    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      m.product_name.toLowerCase().includes(q) ||
      (m.sku && m.sku.toLowerCase().includes(q))
    );
  });

  const totalSkus = materials.length;
  const lowStockCount = materials.filter(m => (m.current_quantity || 0) <= (m.reorder_level || 5) && (m.current_quantity || 0) > 0).length;
  const outOfStockCount = materials.filter(m => (m.current_quantity || 0) <= 0).length;
  const totalStockValue = materials.reduce((sum, m) => sum + ((m.current_quantity || 0) * (m.sale_price || 0)), 0);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Package className="text-primary" size={24} />
            <span>Services Inventory & Spare Parts</span>
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            Spare parts, consumables, cleaning agents & materials used across service bookings
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            disabled={loading}
            className="text-xs h-9 gap-1.5"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </Button>

          <Button
            size="sm"
            onClick={handleOpenAdd}
            className="bg-primary hover:bg-primary/90 text-white text-xs h-9 gap-1.5 font-semibold"
          >
            <Plus size={14} />
            <span>Add Material / Part</span>
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
          <CardContent className="p-3.5 sm:p-4">
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block">
              Total Materials
            </span>
            <div className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white mt-1">
              {totalSkus}
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
          <CardContent className="p-3.5 sm:p-4">
            <span className="text-[11px] font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider block">
              Low Stock Alert
            </span>
            <div className="text-xl sm:text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1">
              {lowStockCount}
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
          <CardContent className="p-3.5 sm:p-4">
            <span className="text-[11px] font-semibold text-rose-600 dark:text-rose-400 uppercase tracking-wider block">
              Out of Stock
            </span>
            <div className="text-xl sm:text-2xl font-bold text-rose-600 dark:text-rose-400 mt-1">
              {outOfStockCount}
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs">
          <CardContent className="p-3.5 sm:p-4">
            <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider block">
              Total Stock Value
            </span>
            <div className="text-xl sm:text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1 font-mono">
              ₹{totalStockValue.toFixed(2)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white dark:bg-slate-900 p-3 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
        <div className="relative w-full sm:w-80">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search material name, SKU, part code..."
            className="pl-9 h-9 text-xs bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
          />
        </div>

        <div className="flex items-center gap-1.5 w-full sm:w-auto">
          <button
            type="button"
            onClick={() => setFilterStock('all')}
            className={`flex-1 sm:flex-none px-3 py-1.5 rounded-xl text-xs font-semibold transition-all border ${
              filterStock === 'all'
                ? 'bg-primary text-white border-primary shadow-xs'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-transparent hover:bg-slate-200'
            }`}
          >
            All Items ({totalSkus})
          </button>
          <button
            type="button"
            onClick={() => setFilterStock('low')}
            className={`flex-1 sm:flex-none px-3 py-1.5 rounded-xl text-xs font-semibold transition-all border ${
              filterStock === 'low'
                ? 'bg-amber-600 text-white border-amber-600 shadow-xs'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-transparent hover:bg-slate-200'
            }`}
          >
            Low Stock ({lowStockCount})
          </button>
          <button
            type="button"
            onClick={() => setFilterStock('out')}
            className={`flex-1 sm:flex-none px-3 py-1.5 rounded-xl text-xs font-semibold transition-all border ${
              filterStock === 'out'
                ? 'bg-rose-600 text-white border-rose-600 shadow-xs'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-transparent hover:bg-slate-200'
            }`}
          >
            Out of Stock ({outOfStockCount})
          </button>
        </div>
      </div>

      {/* Table / List */}
      {loading ? (
        <div className="p-12 text-center text-slate-400 text-sm flex flex-col items-center justify-center gap-2">
          <Loader2 size={24} className="animate-spin text-primary" />
          <span>Loading spare parts and materials...</span>
        </div>
      ) : filteredMaterials.length === 0 ? (
        <div className="p-12 text-center bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-3">
          <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto text-slate-400">
            <Package size={22} />
          </div>
          <h3 className="text-base font-bold text-slate-800 dark:text-white">
            {searchQuery ? 'No materials match your search' : 'No service materials in inventory'}
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm mx-auto">
            Add spare parts, filters, liquids, or replacement components so staff can add them to service invoices.
          </p>
          {!searchQuery && (
            <Button
              size="sm"
              onClick={handleOpenAdd}
              className="bg-primary text-white text-xs h-9 mt-2 font-semibold"
            >
              <Plus size={14} className="mr-1" />
              Add First Material
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-50 dark:bg-slate-800/80 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
                <tr>
                  <th className="p-3.5">Material / Part Name</th>
                  <th className="p-3.5">SKU / Code</th>
                  <th className="p-3.5 text-center">Unit</th>
                  <th className="p-3.5 text-right">Unit Price (₹)</th>
                  <th className="p-3.5 text-center">Current Stock</th>
                  <th className="p-3.5 text-center">Status</th>
                  <th className="p-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {filteredMaterials.map(m => {
                  const qty = m.current_quantity || 0;
                  const reorder = m.reorder_level || 5;
                  const isOut = qty <= 0;
                  const isLow = qty <= reorder && !isOut;

                  return (
                    <tr key={m.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="p-3.5 font-bold text-slate-900 dark:text-white">
                        {m.product_name}
                      </td>
                      <td className="p-3.5 font-mono text-slate-500 text-[11px]">
                        {m.sku || '—'}
                      </td>
                      <td className="p-3.5 text-center text-slate-600 dark:text-slate-300">
                        {m.unit}
                      </td>
                      <td className="p-3.5 text-right font-mono font-bold text-slate-900 dark:text-white">
                        ₹{Number(m.sale_price || 0).toFixed(2)}
                      </td>
                      <td className="p-3.5 text-center font-mono font-bold text-sm">
                        <span className={isOut ? 'text-rose-500' : isLow ? 'text-amber-500' : 'text-slate-800 dark:text-slate-200'}>
                          {qty}
                        </span>
                      </td>
                      <td className="p-3.5 text-center">
                        {isOut ? (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-400">
                            Out of Stock
                          </span>
                        ) : isLow ? (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400">
                            Low Stock
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400">
                            In Stock
                          </span>
                        )}
                      </td>
                      <td className="p-3.5 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenEdit(m)}
                          className="h-7 px-2 text-xs text-slate-600 dark:text-slate-300 hover:text-primary"
                        >
                          <Edit2 size={12} className="mr-1" />
                          <span>Edit</span>
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl p-5 text-white animate-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800 mb-4">
              <h3 className="text-base font-bold flex items-center gap-2">
                <Package size={18} className="text-primary" />
                <span>{editingItem ? 'Edit Material / Part' : 'Add Material / Part'}</span>
              </h3>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSaveItem} className="space-y-3.5 text-xs">
              <div>
                <label className="font-semibold text-slate-300 block mb-1">
                  Material / Item Name <span className="text-rose-500">*</span>
                </label>
                <Input
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g. Copper Pipe 1/2 inch, AC Gas R32 1kg"
                  required
                  className="h-9 text-xs bg-slate-800 border-slate-700 text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="font-semibold text-slate-300 block mb-1">
                    SKU / Part Code
                  </label>
                  <Input
                    value={sku}
                    onChange={e => setSku(e.target.value)}
                    placeholder="e.g. SP-GAS-R32"
                    className="h-9 text-xs bg-slate-800 border-slate-700 text-white font-mono"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-300 block mb-1">
                    Unit of Measure
                  </label>
                  <Input
                    value={unit}
                    onChange={e => setUnit(e.target.value)}
                    placeholder="e.g. Nos, Meter, Kg, Liter"
                    className="h-9 text-xs bg-slate-800 border-slate-700 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="font-semibold text-slate-300 block mb-1">
                    Unit Selling Price (₹) <span className="text-rose-500">*</span>
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={salePrice}
                    onChange={e => setSalePrice(e.target.value)}
                    placeholder="0.00"
                    required
                    className="h-9 text-xs bg-slate-800 border-slate-700 text-white font-mono"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-300 block mb-1">
                    Purchase Cost (₹)
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={purchasePrice}
                    onChange={e => setPurchasePrice(e.target.value)}
                    placeholder="Optional"
                    className="h-9 text-xs bg-slate-800 border-slate-700 text-white font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="font-semibold text-slate-300 block mb-1">
                    Current Stock Quantity
                  </label>
                  <Input
                    type="number"
                    min="0"
                    value={currentQty}
                    onChange={e => setCurrentQty(e.target.value)}
                    placeholder="0"
                    className="h-9 text-xs bg-slate-800 border-slate-700 text-white font-mono"
                  />
                </div>
                <div>
                  <label className="font-semibold text-slate-300 block mb-1">
                    Low Stock Alert Level
                  </label>
                  <Input
                    type="number"
                    min="0"
                    value={reorderLevel}
                    onChange={e => setReorderLevel(e.target.value)}
                    placeholder="5"
                    className="h-9 text-xs bg-slate-800 border-slate-700 text-white font-mono"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowModal(false)}
                  disabled={saving}
                  className="border-slate-700 text-slate-300 text-xs h-9"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={saving}
                  className="bg-primary hover:bg-primary/90 text-white text-xs h-9 px-4 font-semibold"
                >
                  {saving ? <Loader2 size={13} className="animate-spin" /> : 'Save Material'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ServicesInventoryView;
